/**
 * A square with sides of length 1 and a center point at (0.5, 0.5)
 */
class UnitSquare {
  constructor() {
    this.type = 'unitSquare';
    this.cx = 0.5;
    this.cy = 0.5;
    this.x0 = 0;
    this.x1 = 1;
    this.y0 = 0;
    this.y1 = 1;
  }

  static outOfBounds(x, y) {
    return x < 0 || x > 1 || y < 0 || y > 1;
  }

}

/**
 * A circle with a radius of 1 and a center point at (0, 0)
 */
class UnitCircle {
  constructor() {
    this.type = 'unitCircle';
    this.cx = 0;
    this.cy = 0;
    this.r = 1;
  }

  static outOfBounds(x, y) {
    return x * x + y * y > 1;
  }

}

const cartToPolar = (x, y) => {
  return {
    r: Math.sqrt(x * x + y * y),
    theta: Math.atan2(y, x)
  };
};
const polarToCart = (r, theta) => {
  return {
    x: r * Math.cos(theta),
    y: r * Math.sin(theta)
  };
};
const radToDeg = rad => {
  return rad * 180 / Math.PI;
};

/**
 * Represents a relationship between a 1-dimensional input bounded by [0, 1] and a 2-dimensional
 * output bounded by the perimeter of the surface on which the curve lies.
 * 
 * The surface must be either a unit circle (to represent the Hue-Saturation space) or a 
 * unit square (to represent the lightness space).
 * 
 * Curves are not instantiated directly.
 */

class Curve {
  /**
   * Creates a new Curve.
   * @param {object} [options] Optional properties of the curve
   * @param {object} [options.overflow] Defines how to adjust output that extends past the surface perimeter
   * @param {boolean} [options.reverse] Whether to reverse the direction of the curve
   * @param {number} [options.rotation] Z-axis rotation of the curve in radians
   * @param {object} [options.scale] 2-dimensional scale of the curve
   * @param {number} [options.scale.x] Translation along the local X axis
   * @param {number} [options.scale.y] Translation along the local Y axis
   * @param {string} [options.surface] The surface on which to draw the curve
   * @param {object} [options.translation] 2-dimensional translation of the curve
   * @param {number} [options.translation.x] Translation along the local X axis
   * @param {number} [options.translation.y] Translation along the local Y axis
   */
  constructor(options) {
    const {
      overflow,
      reverse,
      rotation,
      scale,
      surface,
      translation
    } = options;
    this.isCurve = true;
    this.surface = {};
    this.scale = {};
    this.translation = {};
    this.setOverflow(overflow);
    this.setReverse(reverse);
    this.setRotation(rotation);
    this.setScale(scale);
    this.setSurface(surface);
    this.setTranslation(translation);
  }

  static getParamSet() {
    const paramsList = ['type', 'overflow', 'reverse', // curve
    'translation', 'translateX', 'translateY', 'scale', 'scaleX', 'scaleY', 'rotation', // tranformations
    'angleStart', 'angleEnd', 'angleOffset', 'radius', // arc
    'variation', // function
    'exponent', // poly
    'amplitude', 'period', // elastic
    'overshoot' // back
    ];
    return paramsList;
  }
  /**
   * Set the curve's surface.
   * @param {object|string} surface The surface on which to draw the curve
   */


  setSurface(surface = 'unitSquare') {
    if (surface === 'unitSquare') {
      this.surface = new UnitSquare();
    } else if (surface === 'unitCircle') {
      this.surface = new UnitCircle();
    } else {
      console.warn("Invalid surface type. Options are 'unitCircle' (for H/S components) or 'unitSquare' (for L component). ", "Using unitSquare instead.");
      surface = new UnitSquare();
    }
  }
  /**
   * Samples the curve and sets clampStart and clampEnd to the input values where the curve intersects the perimeter. If the 
   * outputs at 0 or 1 are inside of the surface then the clampStart and clampEnd are set to 0 and 1, respectively.
   * @param {number} [resolution = 128] Number of samples used to determine the clamp bounds
   */


  setClampBounds(resolution = 128) {
    let prevCoords;
    let clampStart = null;
    let clampEnd = null;
    let i = 0;

    while (i <= resolution && (clampStart === null || clampEnd === null)) {
      const coords = this.getCurveCoordsAt(i / resolution);

      if (i === 0) {
        // if the starting point is inside the surface, then the clamp start is the same as the start
        if (!coords.clamped) clampStart = i / resolution;
      } else {
        // set start clamp if the prev point is outside the surface, but the current point is inside
        if (clampStart === null && prevCoords.clamped && !coords.clamped) {
          clampStart = i / resolution;
        } // set end clamp if the prev point is inside the surface, but the current point is outside


        if (clampEnd === null && !prevCoords.clamped && coords.clamped) {
          clampEnd = i / resolution;
        }
      } // if the entire curve is outside of the surface, set the clampEnd to 0


      if (i === resolution && coords.clamped && clampStart === null && clampEnd === null) {
        clampEnd = 0;
      }

      prevCoords = coords;
      i++;
    }

    this.clampStart = clampStart === null ? 0 : clampStart;
    this.clampEnd = clampEnd === null ? 1 : clampEnd;
  }
  /**
   * Sets the overflow behavior.
   * @param {string} [overflow = 'clamp'] Method for adjusting output that extends past the surface perimeter
   */


  setOverflow(overflow = 'clamp') {
    if (overflow === 'clamp' || overflow === 'project') {
      this.overflow = overflow;
    } else {
      console.warn("Overflow value must be either 'clamp' or 'project'. Using clamp.");
      this.overflow = 'clamp';
    }
  }
  /**
   * Sets the reverse flag.
   * @param {boolean} [reverse = false] Whether to reverse the direction of the curve
   */


  setReverse(reverse = false) {
    this.reverse = reverse;
  }
  /**
   * Sets the rotation of the curve
   * @param {boolean} [rotation = 0] Z-Axis rotation of the curve in radians
   */


  setRotation(rotation = 0) {
    this.rotation = rotation;
  }
  /**
   * Sets the scale of the curve
   * @param {object} [options.scale] 2-dimensional scale of the curve
   * @param {number} [options.scale.x] Scale along the local X axis
   * @param {number} [options.scale.y] Scale along the local Y axis
   */


  setScale(scale) {
    if (typeof scale === 'object' && typeof scale.x === 'number') {
      this.setScaleX(scale.x);
    } else {
      this.setScaleX();
    }

    if (typeof scale === 'object' && typeof scale.y === 'number') {
      this.setScaleY(scale.y);
    } else {
      this.setScaleY();
    }
  }
  /**
   * Sets the X scale of the curve. Default depends on the surface type.
   * @param {number} [x] Scale along the local X axis
   */


  setScaleX(x) {
    if (typeof x === 'number') {
      this.scale.x = x;
    } else {
      this.scale.x = 1;
    }
  }
  /**
   * Sets the Y scale of the curve. Default depends on the surface type.
   * @param {number} [y] Scale along the local Y axis
   */


  setScaleY(y) {
    if (typeof y === 'number') {
      this.scale.y = y;
    } else {
      this.scale.y = 1;
    }
  }
  /**
   * Sets the translation of the curve
   * @param {object} [options.translation] 2-dimensional translation of the curve
   * @param {number} [options.translation.x] Translation along the local X axis
   * @param {number} [options.translation.y] Translation along the local Y axis
   */


  setTranslation(translation) {
    if (typeof translation === 'object' && typeof translation.x === 'number') {
      this.setTranslateX(translation.x);
    } else {
      this.setTranslateX();
    }

    if (typeof translation === 'object' && typeof translation.y === 'number') {
      this.setTranslateY(translation.y);
    } else {
      this.setTranslateY();
    }
  }
  /**
   * Sets the translation of the curve along the local X axis. The default values depends on the surface type.
   * @param {number} [x] Translation along the local X axis
   */


  setTranslateX(x) {
    if (typeof x === 'number') {
      this.translation.x = x;
    } else if (this.surface.type === 'unitSquare') {
      this.translation.x = 0.5;
    } else if (this.surface.type === 'unitCircle') {
      this.translation.x = 0;
    }
  }
  /**
   * Sets the translation of the curve along the local Y axis. The default values depends on the surface type.
   * @param {number} [y] Translation along the local Y axis
   */


  setTranslateY(y) {
    if (typeof y === 'number') {
      this.translation.y = y;
    } else if (this.surface.type === 'unitSquare') {
      this.translation.y = 0.5;
    } else if (this.surface.type === 'unitCircle') {
      this.translation.y = 0;
    }
  }
  /**
   * Returns the x and y coordinates associated with a number n in the range [0, 1].
   * @param {object} n A number between 0 and 1 representing the proportion of the curve to traverse
   * @returns {object.<string, number>} The x and y coordinates of the function at a point n
   */


  getFnCoordsAt(n) {
    if (this.category === 'geometry') {
      return this.fn(n);
    } else if (this.category === 'function') {
      return {
        x: n,
        y: this.fn(n)
      };
    }
  }
  /**
   * Applies transformation to the coordinates of the underlying function for the curve. Returns an object containing
   * the new x and y coordinates and well as a 'clamped' flag that is true for coordinates outside of the surface perimeter.
   * @param {object} n A number between 0 and 1 representing the proportion of the curve to traverse
   * @returns {object.<string, number|boolean>} The x and y coordinates of the curve at a point n
   */


  getCurveCoordsAt(n) {
    if (n < 0 || n > 1) {
      console.error('n must be a number in the range [0, 1]');
      return null;
    } // take mirror of n if reversed


    if (this.reverse) n = 1 - n; // get x and y from the curve's function

    let {
      x,
      y
    } = this.getFnCoordsAt(n); // scale each point

    x *= this.scale.x;
    y *= this.scale.y; // translate each point

    x += this.translation.x;
    y += this.translation.y; // rotate around surface center

    const sin = Math.sin(this.rotation);
    const cos = Math.cos(this.rotation);
    const xRot = (x - this.surface.cx) * cos - (y - this.surface.cy) * sin + this.surface.cx;
    const yRot = (x - this.surface.cy) * sin + (y - this.surface.cy) * cos + this.surface.cy; // clamp methodology depends on the surface type

    if (this.surface.type === 'unitSquare') {
      const clamped = UnitSquare.outOfBounds(xRot, yRot);
      const xClamp = Math.min(1, Math.max(0, xRot));
      const yClamp = Math.min(1, Math.max(0, yRot));
      return {
        x: xClamp,
        y: yClamp,
        clamped
      };
    } else if (this.surface.type === 'unitCircle') {
      // convert to polar in order to clamp the radius
      const clamped = UnitCircle.outOfBounds(xRot, yRot);
      const polarCoords = cartToPolar(xRot, yRot);
      const cartCoordsClamped = polarToCart(Math.max(-1, Math.min(1, polarCoords.r)), polarCoords.theta);
      return {
        x: cartCoordsClamped.x,
        y: cartCoordsClamped.y,
        clamped
      };
    }
  }

}

/**
 * Represents a segment of a circle
 * @extends Curve
 */

class Arc extends Curve {
  /**
   * Creates a new Arc.
   * @param {object} [options] Optional properties of the arc
   * @param {number} [options.radius] Radius of the arc
   * @param {number} [options.angleStart = 0] The angle in radians of the segment start
   * @param {number} [options.angleEnd = 2PI] The angle in radians of the segment end
   * @param {number} [options.angleOffset = 0] The degrees in radians by which the angleStart and angleEnd are offset
   * @param {...*} [curveOptions] See {@link Curve}
   */
  constructor(options) {
    const {
      radius,
      angleStart = 0,
      angleEnd = Math.PI * 2,
      angleOffset = 0
    } = options;
    super({ ...options
    });
    this.type = 'arc';
    this.category = 'geometry';
    this.fn = null;
    this.setRadius(radius);
    this.setAngleStart(angleStart);
    this.setAngleEnd(angleEnd);
    this.setAngleOffset(angleOffset);
    this.setFunction();
  }

  setFunction() {
    this.fn = n => {
      const arcAngle = n * (this.angleEnd - this.angleStart);
      const theta = this.angleOffset + this.angleStart + arcAngle;
      return {
        x: this.radius * Math.cos(theta),
        y: this.radius * Math.sin(theta)
      };
    };
  }
  /**
   * Sets the radius of the arc. If no value is passed a default is set based on the surface type.
   * @param {object} [radius] Radius of the arc
   */


  setRadius(radius) {
    if (typeof radius === 'number') {
      this.radius = radius;
    } else if (this.surface.type === 'unitSquare') {
      this.setRadius(0.25);
    } else if (this.surface.type === 'unitCircle') {
      this.setRadius(0.5);
    }
  }
  /**
   * Sets the angle in radians of the ending point of the segment.
   * @param {object} [angleStart = 0] The angle in radians
   */


  setAngleStart(angleStart = 0) {
    this.angleStart = angleStart;
  }
  /**
   * Sets the angle in radians of the starting point of the segment.
   * @param {object} [angleEnd = 0] The angle in radians
   */


  setAngleEnd(angleEnd = 0) {
    this.angleEnd = angleEnd;
  }
  /**
   * Sets the degrees in radians by which the angleStart and angleEnd are offset
   * @param {object} [angleOffset = 0] The degrees in radians
   */


  setAngleOffset(angleOffset = 0) {
    this.angleOffset = angleOffset;
  }

}

/**
 * Represents a generic function that can be further extended by a child class that specified a 1:1 relationship between a 
 * single input and single output
 * @extends Curve
 * @param {object} [options] Optional properties of the function
 */

class Function extends Curve {
  constructor(options = {}) {
    super({ ...options
    });
    const {
      scale,
      translation
    } = options;
    this.category = 'function';

    this.fn = n => n; // override parents default settings


    this.setTranslation(translation);
    this.setScale(scale);
  }

  setScaleY(y) {
    if (typeof y === 'number') {
      this.scale.y = y;
    } else if (this.surface.type === 'unitSquare') {
      this.scale.y = 0.5;
    } else if (this.surface.type === 'unitCircle') {
      this.scale.y = Math.sin(Math.PI * (9 / 8)) * -2;
    }
  }

  setScaleX(x) {
    if (typeof x === 'number') {
      this.scale.x = x;
    } else if (this.surface.type === 'unitSquare') {
      this.scale.x = 1;
    } else if (this.surface.type === 'unitCircle') {
      this.scale.x = Math.cos(Math.PI * (9 / 8)) * -2;
    }
  }

  setTranslateX(x) {
    if (typeof x === 'number') {
      this.translation.x = x;
    } else if (this.surface.type === 'unitSquare') {
      this.translation.x = 0;
    } else if (this.surface.type === 'unitCircle') {
      this.translation.x = Math.cos(Math.PI * (9 / 8));
    }
  }

  setTranslateY(y) {
    if (typeof y === 'number') {
      this.translation.y = y;
    } else if (this.surface.type === 'unitSquare') {
      this.translation.y = 0.25;
    } else if (this.surface.type === 'unitCircle') {
      this.translation.y = Math.sin(Math.PI * (9 / 8));
    }
  }

}

/**
 * Creates a linear curve.
 * @extends Function
 * @param {object} [options] Optional properties of the function
 */

class Linear extends Function {
  constructor(options) {
    super({ ...options
    });
    this.type = 'linear';
  }

}

var exponent = 3;

var polyIn = (function custom(e) {
  e = +e;

  function polyIn(t) {
    return Math.pow(t, e);
  }

  polyIn.exponent = custom;

  return polyIn;
})(exponent);

var polyOut = (function custom(e) {
  e = +e;

  function polyOut(t) {
    return 1 - Math.pow(1 - t, e);
  }

  polyOut.exponent = custom;

  return polyOut;
})(exponent);

var polyInOut = (function custom(e) {
  e = +e;

  function polyInOut(t) {
    return ((t *= 2) <= 1 ? Math.pow(t, e) : 2 - Math.pow(2 - t, e)) / 2;
  }

  polyInOut.exponent = custom;

  return polyInOut;
})(exponent);

var pi = Math.PI,
    halfPi = pi / 2;

function sinIn(t) {
  return 1 - Math.cos(t * halfPi);
}

function sinOut(t) {
  return Math.sin(t * halfPi);
}

function sinInOut(t) {
  return (1 - Math.cos(pi * t)) / 2;
}

function expIn(t) {
  return Math.pow(2, 10 * t - 10);
}

function expOut(t) {
  return 1 - Math.pow(2, -10 * t);
}

function expInOut(t) {
  return ((t *= 2) <= 1 ? Math.pow(2, 10 * t - 10) : 2 - Math.pow(2, 10 - 10 * t)) / 2;
}

var b1 = 4 / 11,
    b2 = 6 / 11,
    b3 = 8 / 11,
    b4 = 3 / 4,
    b5 = 9 / 11,
    b6 = 10 / 11,
    b7 = 15 / 16,
    b8 = 21 / 22,
    b9 = 63 / 64,
    b0 = 1 / b1 / b1;

function bounceIn(t) {
  return 1 - bounceOut(1 - t);
}

function bounceOut(t) {
  return (t = +t) < b1 ? b0 * t * t : t < b3 ? b0 * (t -= b2) * t + b4 : t < b6 ? b0 * (t -= b5) * t + b7 : b0 * (t -= b8) * t + b9;
}

function bounceInOut(t) {
  return ((t *= 2) <= 1 ? 1 - bounceOut(1 - t) : bounceOut(t - 1) + 1) / 2;
}

var overshoot = 1.70158;

var backIn = (function custom(s) {
  s = +s;

  function backIn(t) {
    return t * t * ((s + 1) * t - s);
  }

  backIn.overshoot = custom;

  return backIn;
})(overshoot);

var backOut = (function custom(s) {
  s = +s;

  function backOut(t) {
    return --t * t * ((s + 1) * t + s) + 1;
  }

  backOut.overshoot = custom;

  return backOut;
})(overshoot);

var backInOut = (function custom(s) {
  s = +s;

  function backInOut(t) {
    return ((t *= 2) < 1 ? t * t * ((s + 1) * t - s) : (t -= 2) * t * ((s + 1) * t + s) + 2) / 2;
  }

  backInOut.overshoot = custom;

  return backInOut;
})(overshoot);

var tau = 2 * Math.PI,
    amplitude = 1,
    period = 0.3;

var elasticIn = (function custom(a, p) {
  var s = Math.asin(1 / (a = Math.max(1, a))) * (p /= tau);

  function elasticIn(t) {
    return a * Math.pow(2, 10 * --t) * Math.sin((s - t) / p);
  }

  elasticIn.amplitude = function(a) { return custom(a, p * tau); };
  elasticIn.period = function(p) { return custom(a, p); };

  return elasticIn;
})(amplitude, period);

var elasticOut = (function custom(a, p) {
  var s = Math.asin(1 / (a = Math.max(1, a))) * (p /= tau);

  function elasticOut(t) {
    return 1 - a * Math.pow(2, -10 * (t = +t)) * Math.sin((t + s) / p);
  }

  elasticOut.amplitude = function(a) { return custom(a, p * tau); };
  elasticOut.period = function(p) { return custom(a, p); };

  return elasticOut;
})(amplitude, period);

var elasticInOut = (function custom(a, p) {
  var s = Math.asin(1 / (a = Math.max(1, a))) * (p /= tau);

  function elasticInOut(t) {
    return ((t = t * 2 - 1) < 0
        ? a * Math.pow(2, 10 * t) * Math.sin((s - t) / p)
        : 2 - a * Math.pow(2, -10 * t) * Math.sin((s + t) / p)) / 2;
  }

  elasticInOut.amplitude = function(a) { return custom(a, p * tau); };
  elasticInOut.period = function(p) { return custom(a, p); };

  return elasticInOut;
})(amplitude, period);

/**
 * Creates an ease "elastic" curve.
 * @extends Function
 * @param {object} [options] Optional properties of the function
 * @param {string} [options.variation] The contour of the function (see https://easings.net)
 * @param {string} [options.exponent] The exponent of the function
 */

class Polynomial extends Function {
  constructor(options) {
    const {
      variation,
      exponent
    } = options;
    super({ ...options
    });
    this.type = 'polynomial';
    this.fn = null;
    this.setExponent(exponent);
    this.setVariation(variation);
  }

  setExponent(e = 3) {
    if (e > 0) {
      this.exponent = e;
      this.setFunction();
    } else {
      console.error('Exponent must be a number greater than 0');
    }
  }
  /**
   * Sets the easing variation. See https://easings.net/
   * @param {string} variation The curve variation ('in', 'out', or 'in-out')
   */


  setVariation(variation = 'in') {
    if (variation === 'in' || variation === 'out' || variation === 'in-out') {
      this.variation = variation;
      this.setFunction();
    } else {
      console.warn("variation must be 'in', 'out', or 'in-out'");
    }
  }
  /**
   * Assigns a function to this class that maps input to output
   */


  setFunction() {
    switch (this.variation) {
      case 'in':
        this.fn = polyIn.exponent(this.exponent);
        break;

      case 'out':
        this.fn = polyOut.exponent(this.exponent);
        break;

      case 'in-out':
        this.fn = polyInOut.exponent(this.exponent);
        break;
    }
  }

}

/**
 * Creates an ease "sinusoidal" curve.
 * @extends Function
 * @param {object} [options] Optional properties of the function
 * @param {string} [options.variation] The contour of the function (see https://easings.net)
 */

class Sinusoidal extends Function {
  constructor(options) {
    const {
      variation
    } = options;
    super({ ...options
    });
    this.type = 'sinusoidal';
    this.fn = null;
    this.setVariation(variation);
  }
  /**
   * Sets the easing variation. See https://easings.net/
   * @param {string} variation The curve variation ('in', 'out', or 'in-out')
   */


  setVariation(variation = 'in') {
    if (variation === 'in' || variation === 'out' || variation === 'in-out') {
      this.variation = variation;
      this.setFunction();
    } else {
      console.warn("variation must be 'in', 'out', or 'in-out'");
    }
  }
  /**
   * Assigns a function to this class that maps input to output
   */


  setFunction() {
    switch (this.variation) {
      case 'in':
        this.fn = sinIn;
        break;

      case 'out':
        this.fn = sinOut;
        break;

      case 'in-out':
        this.fn = sinInOut;
        break;
    }
  }

}

/**
 * Creates an ease "exponential" curve.
 * @extends Function
 * @param {object} [options] Optional properties of the function
 * @param {string} [options.variation] The contour of the function (see https://easings.net)
 */

class Exponential extends Function {
  constructor(options) {
    const {
      variation
    } = options;
    super({ ...options
    });
    this.type = 'exponential';
    this.fn = null;
    this.setVariation(variation);
  }
  /**
   * Sets the easing variation. See https://easings.net/
   * @param {string} variation The curve variation ('in', 'out', or 'in-out')
   */


  setVariation(variation = 'in') {
    if (variation === 'in' || variation === 'out' || variation === 'in-out') {
      this.variation = variation;
      this.setFunction();
    } else {
      console.warn("variation must be 'in', 'out', or 'in-out'");
    }
  }
  /**
   * Assigns a function to this class that maps input to output
   */


  setFunction() {
    switch (this.variation) {
      case 'in':
        this.fn = expIn;
        break;

      case 'out':
        this.fn = expOut;
        break;

      case 'in-out':
        this.fn = expInOut;
        break;
    }
  }

}

/**
 * Creates an "elastic" function.
 * @extends Function
 * @param {object} [options] Optional properties of the function
 * @param {string} [options.amplitude] The amplitude of the function
 * @param {number} [options.period] The period of the function
 */

class Elastic extends Function {
  constructor(options) {
    const {
      variation,
      amplitude,
      period
    } = options;
    super({ ...options
    });
    this.type = 'elastic';
    this.fn = null;
    this.setAmplitude(amplitude);
    this.setPeriod(period);
    this.setVariation(variation);
  }

  setAmplitude(a = 1) {
    if (a >= 1) {
      this.amplitude = a;
      this.setFunction();
    } else {
      console.error('Amplitude must be a number greater than 1');
    }
  }

  setPeriod(p = 0.3) {
    if (p > 0) {
      this.period = p;
      this.setFunction();
    } else {
      console.error('Period must be a number greater than 0.');
    }
  }
  /**
   * Sets the easing variation. See https://easings.net/
   * @param {string} variation The curve variation ('in', 'out', or 'in-out')
   */


  setVariation(variation = 'in') {
    if (variation === 'in' || variation === 'out' || variation === 'in-out') {
      this.variation = variation;
      this.setFunction();
    } else {
      console.warn("variation must be 'in', 'out', or 'in-out'");
    }
  }
  /**
   * Assigns a function to this class that maps input to output
   */


  setFunction() {
    switch (this.variation) {
      case 'in':
        this.fn = elasticIn.amplitude(this.amplitude).period(this.period);
        break;

      case 'out':
        this.fn = elasticOut.amplitude(this.amplitude).period(this.period);
        break;

      case 'in-out':
        this.fn = elasticInOut.amplitude(this.amplitude).period(this.period);
        break;
    }
  }

}

/**
 * Creates an "ease back" function.
 * @extends Function
 * @param {object} [options] Optional properties of the function
 * @param {string} [options.variation] The contour of the function (see https://easings.net)
 * @param {number} [options.overshoot] The degree to which the function "overshoots"
 */

class Back extends Function {
  constructor(options) {
    const {
      variation,
      overshoot
    } = options;
    super({ ...options
    });
    this.type = 'back';
    this.fn = null;
    this.setOvershoot(overshoot);
    this.setVariation(variation);
  }

  setOvershoot(s = 1.70158) {
    this.overshoot = s;
    this.setFunction();
  }
  /**
   * Sets the easing variation. See https://easings.net/
   * @param {string} variation The curve variation ('in', 'out', or 'in-out')
   */


  setVariation(variation = 'in') {
    if (variation === 'in' || variation === 'out' || variation === 'in-out') {
      this.variation = variation;
      this.setFunction();
    } else {
      console.warn("variation must be 'in', 'out', or 'in-out'");
    }
  }
  /**
   * Assigns a function to this class that maps input to output
   */


  setFunction() {
    switch (this.variation) {
      case 'in':
        this.fn = backIn.overshoot(this.overshoot);
        break;

      case 'out':
        this.fn = backOut.overshoot(this.overshoot);
        break;

      case 'in-out':
        this.fn = backInOut.overshoot(this.overshoot);
        break;
    }
  }

}

/**
 * Creates an ease "bounce" curve.
 * @extends Function
 * @param {object} [options] Optional properties of the function
 * @param {string} [options.variation] The contour of the function (see https://easings.net)
 */

class Bounce extends Function {
  constructor(options) {
    const {
      variation
    } = options;
    super({ ...options
    });
    this.type = 'bounce';
    this.fn = null;
    this.setVariation(variation);
  }
  /**
   * Sets the easing variation. See https://easings.net/
   * @param {string} variation The curve variation ('in', 'out', or 'in-out')
   */


  setVariation(variation = 'in') {
    if (variation === 'in' || variation === 'out' || variation === 'in-out') {
      this.variation = variation;
      this.setFunction();
    } else {
      console.warn("variation must be 'in', 'out', or 'in-out'");
    }
  }
  /**
   * Assigns a function to this class that maps input to output
   */


  setFunction() {
    switch (this.variation) {
      case 'in':
        this.fn = bounceIn;
        break;

      case 'out':
        this.fn = bounceOut;
        break;

      case 'in-out':
        this.fn = bounceInOut;
        break;
    }
  }

}

/* 
    This code is based on https://en.wikipedia.org/wiki/HSL_and_HSV
    Free to use for any purpose. No attribution needed.
    For a helpful explanation on HSL and RGB conversion, see:
    https://stackoverflow.com/a/39147465/3064334
*/
const hslToRgb = (h, s, l) => {
  /**
   * Converts an HSL color value to RGB.
   * Returns r, g, and b values in the set [0, 255].
   *
   * @param   {number}  h       The hue value as a degree
   * @param   {number}  s       The saturation value in the range [0, 1]
   * @param   {number}  l       The lightness color value in the range [0, 1]
   * @return  {Object}          The RGB representation
   */
  if (h < 0) h = h % 360 + 360;else h = h % 360;
  let c = (1 - Math.abs(2 * l - 1)) * s;
  let hp = h / 60.0;
  let x = c * (1 - Math.abs(hp % 2 - 1));
  let rgb1;
  if (isNaN(h)) rgb1 = [0, 0, 0];else if (hp <= 1) rgb1 = [c, x, 0];else if (hp <= 2) rgb1 = [x, c, 0];else if (hp <= 3) rgb1 = [0, c, x];else if (hp <= 4) rgb1 = [0, x, c];else if (hp <= 5) rgb1 = [x, 0, c];else if (hp <= 6) rgb1 = [c, 0, x];
  let m = l - c * 0.5;
  return {
    r: Math.round(255 * (rgb1[0] + m)),
    g: Math.round(255 * (rgb1[1] + m)),
    b: Math.round(255 * (rgb1[2] + m))
  };
};
const rgbToHex = (r, g, b) => {
  /**
   * Converts an RGB color value to a hexadecimal string.
   * Returns hex value in the range [#000000, #FFFFFF].
   * See: https://stackoverflow.com/a/5624139/3064334
   *
   * @param   {number}  r       The red color value in the range [0, 255]
   * @param   {number}  g       The green color value in the range [0, 255]
   * @param   {number}  b       The blue color value in the range [0, 255]
   * @return  {String}          The hex representation
   */
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};
const printHsl = (h, s, l) => {
  /**
   * Prints an HSL color in standard CSS format
   * See: https://drafts.csswg.org/css-color/#the-hsl-notation
   *
   * @param   {number}  h       The hue value as a number
   * @param   {number}  s       The saturation value in the range [0, 1]
   * @param   {number}  l       The lightness color value in the range [0, 1]
   * @return  {String}          The HSL color string
   */
  return `hsl(${h}, ${s * 100}%, ${l * 100}%)`;
};
const printRgb = (r, g, b) => {
  /**
   * Prints an RGB color in standard CSS format
   * See: https://drafts.csswg.org/css-color/#the-hsl-notation
   *
   * @param   {number}  r       The red color value in the range [0, 255]
   * @param   {number}  g       The green color value in the range [0, 255]
   * @param   {number}  b       The blue color value in the range [0, 255]
   * @return  {String}          The RGB color string
   */
  return `rgb(${r}, ${g}, ${b})`;
};

const validJson = x => {
  try {
    const o = JSON.parse(x);

    if (o && typeof o === "object") {
      return o;
    }
  } catch (e) {}

  return false;
};

/**
 * A continuous color palette created by overlaying curves onto surfaces in the HSL color space.
 * 
 * A ColorPalette has exactly two curves. The "hs" curve maps to hue and saturation values and lies in a unit circle
 * in the HS space. The "l" curve maps it's y-coordinate to lightness, and lies in a unit square in the L space.
 */

class ColorPalette {
  /**
   * Creates a new Color Palette.
   * @param {object|string} [hsCurve] The curve object or curve type of the "hs" curve
   * @param {object|string} [lCurve] The curve object or curve type of the "l" curve
   * @param {object} [options = {}] Optional properties of the color palette
   * @param {number} [options.start] Starts the palette at a certain point along the curve
   * @param {number} [options.end] Ends the palette at a certain point along the curve
   */
  constructor(hsCurve, lCurve, options = {}) {
    this.setHsCurve(validJson(hsCurve) || hsCurve);
    this.setLCurve(validJson(lCurve) || lCurve);
    this.updateCurveClampBound();
    const {
      start,
      end,
      name,
      author
    } = validJson(options) || options;
    this.setStart(start);
    this.setEnd(end);
    this.setName(name);
    this.setAuthor(author);
  }

  static getParamSet() {
    return ['start', 'end', 'name', 'author'];
  }
  /**
   * Sets a name for this palette
   * @param {string} [name] A name for this palette
   */


  setName(name) {
    if (typeof name === 'string') {
      this.name = name;
    } else {
      this.name = null;
    }
  }
  /**
   * Sets a name for this palette
   * @param {string} [author] A name for this palette
   */


  setAuthor(author) {
    if (typeof author === 'string') {
      this.author = author;
    } else {
      this.author = null;
    }
  }
  /**
   * Sets the "hs" (hue-saturation) curve for this palette.
   * @param {object|string} [hsCurve] An object or string describing the "hs" curve. See {@link Curve}
   * @param {boolean} [applyDefaults = false] If true, applies default params to the new curve. If false, keeps params from 
   * previous curve.
   */


  setHsCurve(hsCurve, applyDefaults = false) {
    const currentParams = applyDefaults || !this.hsCurve ? {} : JSON.parse(this.exportPaletteParams(null, 'hsCurve'));

    if (hsCurve && hsCurve.isCurve) {
      if (hsCurve.surface.type === 'unitCircle') {
        this.hsCurve = hsCurve;
      } else {
        console.error("Due to the nature of the HSL colorspace, the hsCurve is required to have a surface of type 'unitCircle'.");
      }
    } else if (typeof hsCurve === 'object') {
      this.hsCurve = this.initializeCurve(hsCurve.type, {
        surface: 'unitCircle',
        ...currentParams,
        ...hsCurve
      });
    } else if (typeof hsCurve === 'string') {
      this.hsCurve = this.initializeCurve(hsCurve, {
        surface: 'unitCircle',
        ...currentParams
      });
    } else {
      this.hsCurve = this.initializeCurve('exponential', {
        surface: 'unitCircle'
      });
    }
  }
  /**
   * Sets the "l" (lightness) curve for this palette.
   * @param {object|string} [lCurve] An object or string describing the "hs" curve. See {@link Curve}
   * @param {boolean} [applyDefaults = false] If true, applies default params to the new curve. If false, keeps params from 
   * previous curve.
   */


  setLCurve(lCurve, applyDefaults = false) {
    const currentParams = applyDefaults || !this.lCurve ? {} : JSON.parse(this.exportPaletteParams(null, 'lCurve'));

    if (lCurve && lCurve.isCurve) {
      if (lCurve.surface.type === 'unitSquare') {
        this.lCurve = lCurve;
      } else {
        console.error("Due to the nature of the HSL colorspace, the lCurve is required to have a surface of type 'unitSquare'.");
      }
    } else if (typeof lCurve === 'object') {
      this.lCurve = this.initializeCurve(lCurve.type, {
        surface: 'unitSquare',
        ...currentParams,
        ...lCurve
      });
    } else if (typeof lCurve === 'string') {
      this.lCurve = this.initializeCurve(lCurve, {
        surface: 'unitSquare',
        ...currentParams
      });
    } else {
      this.lCurve = this.initializeCurve('linear', {
        surface: 'unitSquare'
      });
    }
  }
  /**
   * Returns a JSON representation of this palette, including representations for each of its curves and the palette itself.
   * The returned string consists of three comma-separated JSON objects which map to hsCurve, lCurve, and paletteParams in the ColorPalette constructor.
   * @param {number} [precision] The number of decimals to include in numerical parameters.
   * @param {string} [subset] Either 'palette', 'hsCurve', or 'lCurve'. Specifies a subset of parameters to export. 
   * @returns {string} The JSON representation of this palette.
   */


  exportPaletteParams(precision, subset) {
    const p = precision || 3; // helper function to convert params to fixed digits

    const pDigits = x => {
      switch (typeof x) {
        case 'number':
          return parseFloat(x.toFixed(p));

        case 'object':
          return Object.assign({}, ...Object.entries(x).map(([k, v]) => ({
            [k]: pDigits(v)
          })));

        default:
          return x;
      }
    }; // get the set of all curve params, and make a collection of defined params for each curve


    const curveParamsSet = Curve.getParamSet();
    const lCurveParams = {};
    const hsCurveParams = {};
    curveParamsSet.forEach(param => {
      if (this.hsCurve[param] !== undefined && this.hsCurve[param] !== null) {
        hsCurveParams[param] = pDigits(this.hsCurve[param]);
      }

      if (this.lCurve[param] !== undefined && this.lCurve[param] !== null) {
        lCurveParams[param] = pDigits(this.lCurve[param]);
      }
    }); // get the set of all palette params, and make a collection of defined params for this palette

    const paletteParamsSet = ColorPalette.getParamSet();
    const paletteParams = {};
    paletteParamsSet.forEach(param => {
      if (this[param] !== undefined && this[param] !== null) {
        paletteParams[param] = pDigits(this[param]);
      }
    });

    switch (subset) {
      case 'palette':
        return JSON.stringify(paletteParams);

      case 'hsCurve':
        return JSON.stringify(hsCurveParams);

      case 'lCurve':
        return JSON.stringify(lCurveParams);

      default:
        return `
            '${JSON.stringify(hsCurveParams)}', \
            '${JSON.stringify(lCurveParams)}', \
            '${JSON.stringify(paletteParams)}'`;
    }
  }
  /**
   * Sets the start point for the palette's curves
   * @param {number} [start] A number in the range [0, 1]. Not to exceed the palette's end point.
   */


  setStart(start) {
    if (typeof start === 'number') {
      this.start = start;
    } else {
      this.start = 0;
    }
  }
  /**
   * Sets the end point for the palette's curves
   * @param {number} [end] A number in the range [0, 1]. Not to be exceeded by the palette's start point.
   */


  setEnd(end) {
    if (typeof end === 'number') {
      this.end = end;
    } else {
      this.end = 1;
    }
  }
  /**
   * Draws a representation of the palette using evenly spaced stops.
   * @param {object} canvas An HTML canvas on which to draw the palette.
   * @param {number} numStops The number of distinct colors to use in the drawing.
   */


  drawDiscretePalette(canvas, numStops) {
    this.updateCurveClampBound();
    const ctx = canvas.getContext('2d');
    const stops = numStops || 12;

    for (let i = 0; i < stops; i++) {
      // get hsl values
      const hsl = this.hslValueAt((i + 0.5) / stops);
      ctx.fillStyle = hsl;
      ctx.fillRect(i * canvas.width / stops, 0, canvas.width * 1.1 / stops, canvas.height);
    }
  }
  /**
   * Draws a representation of the palette using a continuous gradient.
   * @param {object} canvas An HTML canvas on which to draw the palette.
   * @param {number} [resolution = 32] The number of sub-gradients to use.
   */


  drawContinuousPalette(canvas, resolution) {
    this.updateCurveClampBound();
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    const stops = resolution || 32; // draw continuous palette

    for (let i = 0; i <= stops; i++) {
      // get hsl values
      const hsl = this.hslValueAt(i / stops); // add a gradient stop

      gradient.addColorStop(i / stops, hsl);
    }

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  /**
   * Helper function that builds a curve from a specified string.
   * @param {string} curveType A string that maps to one of the supported curve types.
   * @param {object} [options] Options for the curve. See {@link Curve}.
   */


  initializeCurve(curveType, options) {
    switch (curveType) {
      case 'arc':
        return new Arc({ ...options
        });

      case 'linear':
        return new Linear({ ...options
        });

      case 'polynomial':
        return new Polynomial({ ...options
        });

      case 'sinusoidal':
        return new Sinusoidal({ ...options
        });

      case 'exponential':
        return new Exponential({ ...options
        });

      case 'elastic':
        return new Elastic({ ...options
        });

      case 'back':
        return new Back({ ...options
        });

      case 'bounce':
        return new Bounce({ ...options
        });

      default:
        console.warn('Specified curve type is not supported. Using default (linear) instead.');
        return new Linear({ ...options
        });
    }
  }
  /**
   * Helper function to set the clamp bounds for both curves in the palette. This method should be run before
   * after changing the parameters - but before getting a color value - for palette that use curves of overflow type 'clamp'
   */


  updateCurveClampBound() {
    this.hsCurve.overflow === 'clamp' && this.hsCurve.setClampBounds();
    this.lCurve.overflow === 'clamp' && this.lCurve.setClampBounds();
  }
  /**
   * Samples the HS and L curves, and converts their cartesian coordinates to hue, saturation, and lightness values
   * @param {number} n A number in the range [0, 1] that represents the proportion of each curve to traverse before sampling
   * @returns {object} The hue, saturation, and lightness values of the palette at the point n
   */


  getColorValues(n) {
    const hsStart = this.hsCurve.overflow === 'clamp' ? Math.max(this.start, this.hsCurve.clampStart) : this.start;
    const hsEnd = this.hsCurve.overflow === 'clamp' ? Math.min(this.end, this.hsCurve.clampEnd) : this.end;
    const lStart = this.lCurve.overflow === 'clamp' ? Math.max(this.start, this.lCurve.clampStart) : this.start;
    const lEnd = this.lCurve.overflow === 'clamp' ? Math.min(this.end, this.lCurve.clampEnd) : this.end; // get hue and saturation values from the hsCurve

    const hsCartCoords = this.hsCurve.getCurveCoordsAt(hsStart + n * (hsEnd - hsStart));
    const hsPolarCoords = cartToPolar(hsCartCoords.x, hsCartCoords.y);
    const hue = radToDeg(hsPolarCoords.theta) % 360;
    const sat = Math.max(0, Math.min(1, hsPolarCoords.r)); // get lightness values from the lightCurve

    const lCartCoords = this.lCurve.getCurveCoordsAt(lStart + n * (lEnd - lStart));
    const lightness = Math.max(0, Math.min(1, lCartCoords.y));
    return {
      h: hue,
      s: sat,
      l: lightness
    };
  }
  /**
   * Gets the HSL values at the given point in the palette's range.
   * @param {number} n A number in the range [0, 1] that represents the proportion of each curve to traverse before sampling
   * @returns {string} The HSL string of the color at the point n
   */


  hslValueAt(n) {
    const {
      h,
      s,
      l
    } = this.getColorValues(n);
    return printHsl(h, s, l);
  }
  /**
   * Gets the RGB values at the given point in the palette's range.
   * @param {number} n A number in the range [0, 1] that represents the proportion of each curve to traverse before sampling
   * @returns {string} The RGB string of the color at the point n
   */


  rgbValueAt(n) {
    const {
      h,
      s,
      l
    } = this.getColorValues(n);
    const {
      r,
      g,
      b
    } = hslToRgb(h, s, l);
    return printRgb(r, g, b);
  }
  /**
   * Gets the hex values at the given point in the palette's range.
   * @param {number} n A number in the range [0, 1] that represents the proportion of each curve to traverse before sampling
   * @returns {string} The hex string of the color at the point n
   */


  hexValueAt(n) {
    const {
      h,
      s,
      l
    } = this.getColorValues(n);
    const {
      r,
      g,
      b
    } = hslToRgb(h, s, l);
    return rgbToHex(r, g, b);
  }

}

class AllAround extends ColorPalette {
  constructor() {
    super('{"type":"arc","overflow":"clamp","reverse":false,"translation":{"x":0,"y":0},"scale":{"x":1,"y":1},"rotation":0,"angleStart":0,"angleEnd":6.283,"angleOffset":0,"radius":0.5}', '{"type":"arc","overflow":"clamp","reverse":false,"translation":{"x":0.5,"y":0.5},"scale":{"x":1,"y":1},"rotation":0,"angleStart":0,"angleEnd":6.283,"angleOffset":0,"radius":0.25}', '{"start":0,"end":1,"name":"All Around","author":"Color Curves"}');
  }

}

class BeyondBelief extends ColorPalette {
  constructor() {
    super('{"type":"exponential","overflow":"clamp","reverse":false,"translation":{"x":-0.88,"y":-0.84},"scale":{"x":1.848,"y":0.765},"rotation":10.556,"variation":"in"}', '{"type":"sinusoidal","overflow":"clamp","reverse":false,"translation":{"x":-0.1,"y":0.25},"scale":{"x":1.02,"y":0.29},"rotation":-1.257,"variation":"in"}', '{"start":0.23,"end":1,"name":"Beyond Belief","author":"Color Curves"}');
  }

}

class CandyPaint extends ColorPalette {
  constructor() {
    super('{"type":"exponential","overflow":"clamp","reverse":false,"translation":{"x":-0.924,"y":-0.45},"scale":{"x":1.848,"y":0.77},"rotation":0,"variation":"in"}', '{"type":"linear","overflow":"clamp","reverse":false,"translation":{"x":0,"y":0.25},"scale":{"x":1,"y":0.5},"rotation":4.541}', '{"start":0.02,"end":1,"name":"Candy Paint","author":"Color Curves"}');
  }

}

class CoralScrub extends ColorPalette {
  constructor() {
    super('{"type":"linear","overflow":"clamp","reverse":false,"translation":{"x":-0.97,"y":0.11},"scale":{"x":1.8,"y":0.52},"rotation":0}', '{"type":"linear","overflow":"clamp","reverse":false,"translation":{"x":0,"y":0.39},"scale":{"x":0.93,"y":0.27},"rotation":0}', '{"start":0,"end":1,"name":"Coral Scrub","author":"Color Curves"}');
  }

}

class GoldfishDeluxe extends ColorPalette {
  constructor() {
    super('{"type":"exponential","overflow":"clamp","reverse":false,"translation":{"x":-0.924,"y":-0.383},"scale":{"x":1.848,"y":0.765},"rotation":3.171,"variation":"in"}', '{"type":"linear","overflow":"clamp","reverse":false,"translation":{"x":0,"y":0.05},"scale":{"x":1,"y":0.44},"rotation":2.69}', '{"start":0,"end":1,"name":"Goldfish Deluxe","author":"Color Curves"}');
  }

}

class PhytoPlankton extends ColorPalette {
  constructor() {
    super('{"type":"exponential","overflow":"clamp","reverse":false,"translation":{"x":-1.12,"y":0.5},"scale":{"x":2.17,"y":0.765},"rotation":0.691,"variation":"in"}', '{"type":"polynomial","overflow":"clamp","reverse":false,"translation":{"x":0,"y":0.44},"scale":{"x":1,"y":0.41},"rotation":6.974,"variation":"in","exponent":3}', '{"name": "Phyto Plankton", "author": "Color Curves", "start":0,"end":0.72}');
  }

}

class PolarBeyond extends ColorPalette {
  constructor() {
    super('{"type":"bounce","overflow":"clamp","reverse":false,"translation":{"x":-0.924,"y":-0.92},"scale":{"x":1.848,"y":0.45},"rotation":4.3,"variation":"in"}', '{"type":"exponential","overflow":"clamp","reverse":false,"translation":{"x":0,"y":0.43},"scale":{"x":1,"y":0.19},"rotation":0,"variation":"in-out"}', '{"start":0.53,"end":0.87,"name":"Polar Beyond","author":"Color Curves"}');
  }

}

class PowerWashed extends ColorPalette {
  constructor() {
    super('{"type":"polynomial","overflow":"clamp","reverse":true,"translation":{"x":-0.68,"y":-0.09},"scale":{"x":1.21,"y":0.25},"rotation":2.827,"variation":"in","exponent":1.74}', '{"type":"sinusoidal","overflow":"clamp","reverse":true,"translation":{"x":0,"y":0.73},"scale":{"x":1,"y":-0.07},"rotation":0,"variation":"in"}', '{"name":"Power Washed","author":"Color Curves","start":0,"end":0.86}');
  }

}

class StockImage extends ColorPalette {
  constructor() {
    super('{"type":"elastic","overflow":"clamp","reverse":false,"translation":{"x":-2.49,"y":-0.383},"scale":{"x":1.848,"y":0.765},"rotation":0,"variation":"in","amplitude":1,"period":0.3}', '{"type":"arc","overflow":"clamp","reverse":false,"translation":{"x":0.5,"y":-0.71},"scale":{"x":1,"y":5.66},"rotation":0,"angleStart":0,"angleEnd":1.319,"angleOffset":0.942,"radius":0.25}', '{"name": "Stock Image", "author": "Color Curves", "start":0,"end":1}');
  }

}

class TrixSky extends ColorPalette {
  constructor() {
    super('{"type":"exponential","overflow":"clamp","reverse":false,"translation":{"x":-1.31,"y":-0.383},"scale":{"x":1.848,"y":0.765},"rotation":5.81,"variation":"in"}', '{"type":"linear","overflow":"clamp","reverse":false,"translation":{"x":0,"y":0.34},"scale":{"x":1,"y":0.5},"rotation":0}', '{"start":0,"end":1,"name":"Trix Sky","author":"Color Curves"}');
  }

}

class UnAmerican extends ColorPalette {
  constructor() {
    super('{"type":"polynomial","overflow":"clamp","reverse":false,"translation":{"x":-0.924,"y":-0.383},"scale":{"x":1.848,"y":0.765},"rotation":2.75,"variation":"out","exponent":3}', '{"type":"linear","overflow":"clamp","reverse":false,"translation":{"x":0,"y":0.25},"scale":{"x":1,"y":0.74},"rotation":0}', '{"start":0,"end":1,"name":"Un-American","author":"Color Curves"}');
  }

}

class WarmMagma extends ColorPalette {
  constructor() {
    super('{"type":"exponential","overflow":"clamp","reverse":false,"translation":{"x":-0.66,"y":-0.58},"scale":{"x":1.61,"y":0.76},"rotation":0.84,"variation":"in"}', '{"type":"sinusoidal","overflow":"clamp","reverse":false,"translation":{"x":0,"y":0.37},"scale":{"x":1,"y":0.24},"rotation":0,"variation":"out"}', '{"start":0.05,"end":1,"name":"Warm Magma","author":"Color Curves"}');
  }

}

class OnVacation extends ColorPalette {
  constructor() {
    super('{"type":"exponential","overflow":"clamp","reverse":false,"translation":{"x":-0.94,"y":-0.383},"scale":{"x":1.94,"y":0.765},"rotation":-0.534,"variation":"in"}', '{"type":"back","overflow":"clamp","reverse":false,"translation":{"x":0,"y":0.48},"scale":{"x":1,"y":0.43},"rotation":-0.346,"variation":"in","overshoot":1.702}', '{"start":0,"end":1,"name":"On Vacation","author":"Color Curves"}');
  }

}

export { AllAround, BeyondBelief, CandyPaint, ColorPalette, CoralScrub, Curve, GoldfishDeluxe, OnVacation, PhytoPlankton, PolarBeyond, PowerWashed, StockImage, TrixSky, UnAmerican, WarmMagma };
